import { z } from 'zod';
import { LangCodeSchema, TimestampSchema, UuidSchema } from './primitives';

// =============================================================================
// Recurrence
// =============================================================================

/**
 * Minimal RRULE-lite validator.
 *
 * MVP supports:
 *   FREQ=DAILY
 *   FREQ=WEEKLY[;BYDAY=MO,TU,WE,TH,FR,SA,SU]
 *   FREQ=MONTHLY
 *   FREQ=YEARLY
 *
 * Anything richer is accepted as an opaque string today and simply falls back
 * to single-instance behaviour in the expander. The schema therefore only
 * asserts it's a reasonable length — full RFC 5545 validation is a wave-5
 * concern.
 */
export const RecurrenceRuleSchema = z
  .string()
  .min(1)
  .max(500)
  .refine((v) => /FREQ=(DAILY|WEEKLY|MONTHLY|YEARLY)/i.test(v), {
    message: 'recurrence_rule must contain FREQ=(DAILY|WEEKLY|MONTHLY|YEARLY)',
  });

export const AttendeeRoleSchema = z.enum(['organizer', 'required', 'optional']);
export type AttendeeRole = z.infer<typeof AttendeeRoleSchema>;

export const AttendeeResponseSchema = z.enum(['pending', 'accepted', 'declined', 'tentative']);
export type AttendeeResponse = z.infer<typeof AttendeeResponseSchema>;

// =============================================================================
// Event (row)
// =============================================================================

export const EventSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  created_by: UuidSchema,
  title: z.string().min(1).max(500),
  description: z.string().max(10_000).nullable(),
  source_lang: LangCodeSchema,
  location: z.string().max(500).nullable(),
  starts_at: TimestampSchema,
  ends_at: TimestampSchema,
  all_day: z.boolean(),
  timezone: z.string().min(1).max(64),
  recurrence_rule: z.string().max(500).nullable(),
  recurrence_until: TimestampSchema.nullable(),
  parent_event_id: UuidSchema.nullable(),
  color: z.string().max(16).nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Event = z.infer<typeof EventSchema>;

// =============================================================================
// Event create / update inputs
// =============================================================================

export const EventCreateInputSchema = z
  .object({
    title: z.string().min(1).max(500),
    description: z.string().max(10_000).optional(),
    location: z.string().max(500).optional(),
    starts_at: TimestampSchema,
    ends_at: TimestampSchema,
    all_day: z.boolean().default(false),
    timezone: z.string().min(1).max(64).default('UTC'),
    recurrence_rule: RecurrenceRuleSchema.optional(),
    recurrence_until: TimestampSchema.optional(),
    color: z.string().max(16).optional(),
    /** Optional initial attendees (user ids). The creator is always an organizer. */
    attendee_ids: z.array(UuidSchema).max(200).optional(),
  })
  .superRefine((val, ctx) => {
    if (new Date(val.ends_at).getTime() < new Date(val.starts_at).getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ends_at'],
        message: 'ends_at must be >= starts_at',
      });
    }
  });
export type EventCreateInput = z.infer<typeof EventCreateInputSchema>;

export const EventUpdateInputSchema = z
  .object({
    title: z.string().min(1).max(500),
    description: z.string().max(10_000).nullable(),
    location: z.string().max(500).nullable(),
    starts_at: TimestampSchema,
    ends_at: TimestampSchema,
    all_day: z.boolean(),
    timezone: z.string().min(1).max(64),
    recurrence_rule: RecurrenceRuleSchema.nullable(),
    recurrence_until: TimestampSchema.nullable(),
    color: z.string().max(16).nullable(),
  })
  .partial();
export type EventUpdateInput = z.infer<typeof EventUpdateInputSchema>;

// =============================================================================
// Event attendee
// =============================================================================

export const EventAttendeeSchema = z.object({
  event_id: UuidSchema,
  user_id: UuidSchema,
  tenant_id: UuidSchema,
  role: AttendeeRoleSchema,
  response: AttendeeResponseSchema,
  responded_at: TimestampSchema.nullable(),
});
export type EventAttendee = z.infer<typeof EventAttendeeSchema>;

export const EventAttendeeAddInputSchema = z.object({
  user_id: UuidSchema,
  role: AttendeeRoleSchema.default('required'),
});
export type EventAttendeeAddInput = z.infer<typeof EventAttendeeAddInputSchema>;

export const EventAttendeeResponseInputSchema = z.object({
  response: AttendeeResponseSchema,
});
export type EventAttendeeResponseInput = z.infer<typeof EventAttendeeResponseInputSchema>;

// =============================================================================
// Expanded occurrence
// =============================================================================

/**
 * An expanded instance of a (possibly recurring) event returned by
 * GET /calendar/events. When the source is a single event with no rrule,
 * master_event_id === id.
 */
export const EventOccurrenceSchema = EventSchema.extend({
  /** The row id of the master event — same as id for non-recurring. */
  master_event_id: UuidSchema,
  /** Occurrence-specific starts_at (may differ from master.starts_at). */
  occurrence_starts_at: TimestampSchema,
  /** Occurrence-specific ends_at. */
  occurrence_ends_at: TimestampSchema,
});
export type EventOccurrence = z.infer<typeof EventOccurrenceSchema>;

// =============================================================================
// Range query
// =============================================================================

export const CalendarRangeQuerySchema = z
  .object({
    from: TimestampSchema,
    to: TimestampSchema,
  })
  .refine((v) => new Date(v.to).getTime() >= new Date(v.from).getTime(), {
    message: 'to must be >= from',
    path: ['to'],
  });
export type CalendarRangeQuery = z.infer<typeof CalendarRangeQuerySchema>;
